import React, { useState, useEffect } from 'react';
import AuthShell from './AuthShell';
import { useNavigate } from 'react-router-dom';

const EyeIcon = ({ visible }) =>
  visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

const Login = ({ setLoggedInUser }) => {
  // --- SHARED STATE ---
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // --- PRODUCTION (Verification) STATE ---
  const [showVerification, setShowVerification] = useState(false);
  const [verifCode, setVerifCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isFocused, setIsFocused] = useState(false); // <--- Add this

  // ---------------------------------------------------------
  // PRODUCTION LOGIC (Email Verification System)
  // ---------------------------------------------------------

  // STEP 1: Verify Password & Trigger Email
  const handleStartLogin = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch('https://vicktoria.ai/api/email-verification/login-check', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      if (response.ok) {
        setShowVerification(true);
      } else {
        const result = await response.json();
        setError(result.error || "Login failed.");
      }
    } catch (err) {
      setError("Unable to connect to the server.");
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Verify Code & Finalize Session
  const finalizeLogin = async () => {
    setIsVerifying(true);
    setError("");

    try {
      // A. Verify the email code
      const verifCheck = await fetch('https://vicktoria.ai/api/email-verification/verify', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase(), userCode: verifCode }),
        //eventually this toLowercase won't be necessary but for now we might have uppercase in the DB

      });

      if (!verifCheck.ok) {
        const result = await verifCheck.json();
        setError(result.error || "Invalid code.");
        setIsVerifying(false);
        return;
      }

      // B. Password verified + Code verified -> Create Session
      const response = await fetch('https://vicktoria.ai/api/login', {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ email: email.toLowerCase(), password }),
        //eventually this toLowercase won't be necessary but for now we might have uppercase in the DB
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "Login failed.");
        setIsVerifying(false);
      } else {
        localStorage.setItem("token", result.token);
        localStorage.setItem("loggedInUser", JSON.stringify(result.user));
        setLoggedInUser(result.user);
        const destination = localStorage.getItem('postLoginRedirect') || '/';
        navigate(destination);
      }
    } catch (err) {
      setError("Unable to complete login.");
      setIsVerifying(false);
    }
  };

  // Auto-submit when 6 digits are reached (Production Only)
  useEffect(() => {
    if (verifCode.length === 6) {
      finalizeLogin();
    }
  }, [verifCode]);

  // --- NEW FUNCTIONS FOR VERIFICATION UI ---
  const handleClearEntry = () => {
    setVerifCode("");
  };

  const handleResendCode = async () => {
    setError("");
    try {
      // UPDATED ENDPOINT
      const response = await fetch('https://vicktoria.ai/api/email-verification/resend-login', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      if (response.ok) {
        setError("A new code has been sent to your email.");
      } else {
        const result = await response.json();
        setError(result.error || "Failed to send code.");
      }
    } catch (err) {
      setError("Unable to connect to the server.");
    }
  };
  // ---------------------------------------------------------
  // DEVELOPMENT LOGIC (Direct Login)
  // ---------------------------------------------------------

  const handleDevLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch('https://vicktoria.ai/api/login', {
        method: "POST",
        // -------------------------------------------------
        credentials: "include", // <--- ADD THIS LINE !!!
        // -------------------------------------------------
        body: JSON.stringify({ email: email.toLowerCase(), password }),
        headers: { "Content-Type": "application/json" },
      });

      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const textError = await response.text();
        throw new Error(textError || "Server error occurred.");
      }

      if (!response.ok) {
        setError(result.error || "Login failed.");
      } else {
        // SUCCESS: Result now contains { token, user }
        // 1. Save the token for future authenticated requests
        localStorage.setItem("token", result.token);

        // 2. Save the user object (now including the 'name' field)
        localStorage.setItem("loggedInUser", JSON.stringify(result.user));

        setLoggedInUser(result.user);
        const destination = localStorage.getItem('postLoginRedirect') || '/';
        navigate(destination);
      }
    } catch (err) {
      console.error("Login attempt failed:", err);
      setError("Unable to connect to the login server.");
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // THE SWITCH
  // =========================================================

  if (!window.MY_SWITCH.isDev) {
    // --- PRODUCTION RENDERING ---

    // Styles for Production
    const inputContainerStyle = { marginBottom: '20px', textAlign: 'center' };
    const labelStyle = { display: 'block', color: '#2B4B6F', fontSize: '18px', marginBottom: '8px' };
    const inputStyle = { width: '100%', maxWidth: '300px', padding: '10px', borderRadius: '8px', border: '2px solid #D1DEE6', backgroundColor: '#F8FAFC', textAlign: 'center', fontSize: '16px', boxSizing: 'border-box' };
    const passwordInputStyle = { ...inputStyle, paddingRight: '36px' };
    const eyeBtnStyle = { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#2B4B6F', display: 'flex', alignItems: 'center' };
    const boxStyle = { width: '45px', height: '55px', border: '1px solid #2B4B6F', display: 'inline-block', margin: '0 5px', backgroundColor: 'white', fontSize: '24px', lineHeight: '55px', textAlign: 'center', verticalAlign: 'top' };

    return (
      <AuthShell title={showVerification ? "Verify Your Login" : "Sign in to an existing account"}>
        <style>{`
          @media screen and (max-width: 600px) {
            .verification-box { width: 35px !important; height: 45px !important; line-height: 45px !important; margin: 0 3px !important; font-size: 18px !important; }
          }
          @media screen and (max-width: 400px) {
            .verification-box { width: 30px !important; height: 40px !important; line-height: 40px !important; margin: 0 3px !important; font-size: 16px !important; }
          }
          @media screen and (max-width: 350px) {
            .verification-box { width: 20px !important; height: 30px !important; line-height: 30px !important; margin: 0 4px !important; font-size: 14px !important; }
          }
        `}</style>

        {!showVerification ? (
          <form onSubmit={handleStartLogin}>
            <div style={inputContainerStyle}>
              <label style={labelStyle}>Email Address</label>
              <input type="email" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
            </div>

            <div style={inputContainerStyle}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '300px' }}>
                <input type={showPassword ? "text" : "password"} style={passwordInputStyle} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="**********" required />
                <button style={eyeBtnStyle} onClick={() => setShowPassword(!showPassword)} type="button" tabIndex={-1}><EyeIcon visible={showPassword} /></button>
              </div>
            </div>

            <button type="submit" style={{ backgroundColor: '#4A8EB3', color: 'white', border: 'none', padding: '12px 45px', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>
              {isLoading ? "Checking..." : "Submit"}
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#2B4B6F', marginBottom: '20px' }}>Enter the 6-digit code sent to <b>{email}</b></p>
            <div style={{ position: 'relative', marginBottom: '25px' }}>
              <input type="text" maxLength="6" autoFocus value={verifCode} disabled={isVerifying} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} onChange={(e) => setVerifCode(e.target.value.replace(/\D/g, ""))}
                style={{ position: 'absolute', opacity: 0, width: '100%', height: '55px', zIndex: 2, cursor: 'pointer' }} />
              {[...Array(6)].map((_, i) => {
                // LOGIC: Is this the specific box waiting for input?
                const isActive = isFocused && verifCode.length === i;
                const hasValue = verifCode[i] !== undefined;

                return (
                  <div
                    key={i}
                    className="verification-box"
                    style={{
                      ...boxStyle,
                      // If active, use a thicker, darker border. If not, use standard logic.
                      border: isActive ? '2px solid #2B4B6F' : `1px solid ${isVerifying ? '#D1DEE6' : '#2B4B6F'}`,
                      // If active, add a slight shadow glow
                      boxShadow: isActive ? '0 0 5px rgba(43, 75, 111, 0.3)' : 'none',
                      // If it's a placeholder, make it gray. If it's a real value, make it black.
                      color: hasValue ? 'black' : '#D1DEE6',
                      display: 'inline-block',
                      verticalAlign: 'top'
                    }}
                  >
                    {/* Show the typed number OR the placeholder index (1-6) */}
                    {hasValue ? verifCode[i] : i + 1}
                  </div>
                );
              })}
            </div>
            {isVerifying && <p style={{ color: '#4A8EB3' }}>Verifying...</p>}
            <span style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F' }} onClick={() => setShowVerification(false)}>Back to form</span>
            {showVerification && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                <span
                  onClick={handleClearEntry}
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F' }}
                >
                  Clear Entry
                </span>
                <span
                  onClick={handleResendCode}
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F' }}
                >
                  Request New Code
                </span>
              </div>
            )}
          </div>
        )}

        {error && <div style={{ color: '#D0021B', marginTop: '15px', fontSize: '14px', textAlign: 'center' }}>{error}</div>}
        {/* --- NEW BUTTONS --- */}

        {!showVerification && (
          <div style={{ marginTop: '25px', textAlign: 'center' }}>
            <style>{`
              .signup-cta-link {
                color: #2B4B6F;
                cursor: pointer;
                display: inline-block;
                text-decoration: underline;
                font-weight: bold;
                font-size: 20px;
                margin-top: 6px;
                transition: color 0.15s ease;
              }
              .signup-cta-link:hover {
                color: #4a7094;
              }
            `}</style>
            <span style={{ color: '#2B4B6F', cursor: 'pointer', display: 'block', marginTop: '20px', textDecoration: 'underline' }} onClick={() => navigate('/forgot-password')}>Forgot your password?</span>

            {/* Sign-up CTA: small question label + visually loud "Sign up" link.
                Single bold span used to read as one statement, so users missed
                that "Sign up here" was actionable. Two-tier styling fixes that. */}
            <div style={{ marginTop: '30px' }}>
              <span style={{ color: '#2B4B6F', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                Don't have an account yet?
              </span>
              <span
                className="signup-cta-link"
                onClick={() => navigate('/signup')}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/signup'); }}
              >
                Sign up for an account here
              </span>
            </div>

            <span style={{ fontSize: '12px', color: '#2B4B6F', fontStyle: 'italic', display: 'block', marginTop: '30px' }}>Vicktoria runs more smoothly on Google Chrome</span>
          </div>
        )}
      </AuthShell>
    );
  }

  // =========================================================
  // DEVELOPMENT RENDERING
  // =========================================================

  // Styles defined for a pure visual match to your screenshot (Dev)
  const inputContainerStyle = {
    marginBottom: '20px',
    textAlign: 'center'
  };

  const labelStyle = {
    display: 'block',
    color: '#2B4B6F',
    fontSize: '18px',
    marginBottom: '8px'
  };

  const inputStyle = {
    width: '100%',
    maxWidth: '300px',
    padding: '10px 36px 10px 10px',
    borderRadius: '8px',
    border: '2px solid #D1DEE6',
    backgroundColor: '#F8FAFC',
    textAlign: 'center',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const eyeBtnStyle = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#2B4B6F',
    display: 'flex',
    alignItems: 'center',
  };

  const submitButtonStyle = {
    backgroundColor: '#4A8EB3',
    color: 'white',
    border: 'none',
    padding: '12px 45px',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '10px'
  };

  const navLinkStyle = {
    color: '#2B4B6F',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: '16px',
    display: 'block',
    marginTop: '20px'
  };

  return (
    <AuthShell title="Sign in to an existing account">
      {/* Display real errors if login fails */}
      {error && <div style={{ color: 'red', marginBottom: '15px', fontSize: '14px' }}>{error}</div>}

      <form onSubmit={handleDevLogin}>
        <div style={inputContainerStyle}>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            style={inputStyle}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
          />
        </div>

        <div style={inputContainerStyle}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '300px' }}>
            <input
              type={showPassword ? "text" : "password"}
              style={inputStyle}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="**********"
              required
            />
            <button style={eyeBtnStyle} onClick={() => setShowPassword(!showPassword)} type="button" tabIndex={-1}>
              <EyeIcon visible={showPassword} />
            </button>
          </div>
        </div>

        <button type="submit" style={submitButtonStyle}>
          Submit
        </button>
      </form>
      {/*navigation done via setCurrentView */}
      <div style={{ marginTop: '25px' }}>
        <style>{`
          .signup-cta-link {
            color: #2B4B6F;
            cursor: pointer;
            display: inline-block;
            text-decoration: underline;
            font-weight: bold;
            font-size: 20px;
            margin-top: 6px;
            transition: color 0.15s ease;
          }
          .signup-cta-link:hover {
            color: #4a7094;
          }
        `}</style>

        <span
          style={navLinkStyle}
          onClick={() => navigate('/forgot-password')}>
          Forgot your password?
        </span>

        {/* Sign-up CTA: small question label + visually loud "Sign up" link.
            Single bold span used to read as one statement, so users missed
            that the second line was actionable. Two-tier styling fixes that. */}
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <span style={{ color: '#2B4B6F', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
            Don't have an account yet?
          </span>
          <span
            className="signup-cta-link"
            onClick={() => navigate('/signup')}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/signup'); }}
          >
            Sign up for an account here
          </span>
        </div>

        <span style={{ fontSize: '12px', color: '#2B4B6F', fontStyle: 'italic', display: 'block', marginTop: '30px' }}>Vicktoria runs more smoothly on Google Chrome</span>
      </div>
    </AuthShell>
  );
};

export default Login;